(ns yardwerkz.api-helpers
  "Convenience helpers for the new api/* stubs.
   Bridges ctx/await with auth caching from capra-dispatch.

   Two patterns:
   1. (api/request! token-fn #(api/job-list %1 {:auth %2}))
      — single call with auth, returns Promise<value>
   2. (api/request! token-fn (fn [ctx auth]
                               [(api/job-list ctx {:auth auth})
                                (api/service-list ctx {:auth auth})]))
      — multiple calls batched in one round trip, returns Promise<[v1 v2]>"
  (:require
   [capra.core :as capra]
   [yardwerkz.capra-client :as capra-client]
   [yardwerkz.capra-dispatch :as dispatch]))

(def ^:private request-timeout-ms
  "Safety timeout for RPC requests. If the server doesn't respond within
   this window, reject the promise so the UI never stays stuck."
  20000)

(defn- with-timeout
  "Wrap a promise with a timeout. Rejects with ex-info if not settled in time."
  [promise timeout-ms]
  (let [timer-id (atom nil)]
    (js/Promise.race
     #js [(-> promise
              (.finally (fn [] (js/clearTimeout @timer-id))))
          (js/Promise.
           (fn [_ reject]
             (reset! timer-id
                     (js/setTimeout
                      #(reject (ex-info "Request timed out" {:code :error/timeout}))
                      timeout-ms))))])))

(defn request!
  "Call api/* stubs with automatic auth. Returns Promise.

   get-token-fn: token provider (from effects/token-provider)
   call-fn:      (fn [ctx identity] ref-or-refs) — builds pipeline ops

   If call-fn returns a single PendingRef → Promise<value>.
   If call-fn returns a vector of PendingRefs → Promise<[v1 v2 ...]>."
  [get-token-fn call-fn]
  (with-timeout
    (-> (dispatch/authenticate! get-token-fn)
        (.then (fn [identity]
                 (let [ctx  (capra/ctx @capra-client/client)
                       result (call-fn ctx identity)]
                   (if (vector? result)
                     (apply capra/await ctx result)
                     (capra/await ctx result))))))
    request-timeout-ms))
